import {
  ChevronUpIcon,
  ClockIcon,
  InformationCircleIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import HackathonStagesService from '@/components/projects/hackathon/form/HackathonStagesService';
import IconButton from '@/components/ui/custom/buttons/IconButton';
import Tag from '@/components/ui/custom/dataDisplay/Tag';
import Text from '@/components/ui/custom/dataDisplay/Text';
import { Tooltip } from '@/components/ui/custom/dataDisplay/Tooltip';
import ModalDisable from '@/components/ui/custom/modals/ModalDisable';
import BodyCell from '@/components/ui/custom/table/BodyCell';
import Row from '@/components/ui/custom/table/Row';
import { deleteHackathon } from '@/data/services/hackathonServices';
import Hackathon from '@/models/Hackathon';
import { editFormAnimation } from '@/utils/animations/formAnimations';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { KlassHeader } from '@/utils/HeaderTypes';
import Observable from '@/utils/observers/ObserverPattern';
import alert from '@/utils/shared/alert';
import { useMutation } from '@tanstack/react-query';
import EditHackathon from './EditHackathon';

interface DataHAckathonProps {
  hackathons: Hackathon[];
  titles: KlassHeader;
  onDelete?: () => void;
}

export default function DataHackathonTable({
  hackathons,
  titles,
  onDelete,
}: DataHAckathonProps) {
  const observer = new Observable<number>();

  return (
    <tbody className="bg-transparent flex flex-col gap-y-2.5">
      {hackathons.map(hackathon => {
        return (
          <RenderRow
            key={hackathon.id}
            hackathon={hackathon}
            titles={titles}
            onDelete={onDelete}
            observer={observer}
          />
        );
      })}
    </tbody>
  );
}

interface RenderRowProps {
  hackathon: Hackathon;
  titles: KlassHeader;
  observer?: Observable<number>;
  onDelete?: () => void;
}

type Tabs = 'info' | 'hackathonStages';

const RenderRow = ({
  hackathon,
  observer,
  titles,
  onDelete: onDeleteExternal,
}: RenderRowProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'manageHackathon',
  });
  const { t: tCommon } = useTranslation('translation', {
    keyPrefix: 'common',
  });

  const [modal, setModal] = useState(false);

  const { mutate: onDelete, isLoading: isDeleting } = useMutation(
    deleteHackathon,
    {
      onError: err => alert.error(getErrorMessage(err)),
      onSuccess: onDeleteExternal,
    },
  );

  const [viewInformations, setViewInformations] = useState(false);

  const [tab, setTab] = useState<Tabs>('info');

  const toogleViewInformations = (newTab?: Tabs) => {
    if (tab !== newTab) {
      if (newTab) {
        setTab(newTab);
      }
      setViewInformations(true);
      return;
    }
    if (!viewInformations) observer?.notifyAll(hackathon.id);
    setViewInformations(old => !old);
    if (tab) setTab(tab);
  };

  useEffect(() => {
    if (hackathon.id) {
      observer?.subscribe({
        id: hackathon.id,
        action: () => {
          setViewInformations(false);
        },
      });
    }
  }, [hackathon.id, observer]);

  return (
    <Row testId="classRow">
      <BodyCell className="justify-between p-0">
        <div
          style={{ width: `${titles.klass.size}%` }}
          className="flex items-center"
          onClick={() => toogleViewInformations(tab)}
        >
          <IconButton
            className="transition ease-in-out duration-150"
            icon={
              <ChevronUpIconWithAnimation
                animate={{
                  rotate: !viewInformations ? 180 : 0,
                }}
                className="w-3 h-3"
              />
            }
          />

          <div className="flex gap-4 items-center">
            <Text
              text={'Hackathon' + hackathon.edition}
              className="cursor-pointer pl-5 self-center h-fit capitalize"
              format="rubik-400"
            />
          </div>
        </div>

        <div style={{ width: `${titles.start.size}%` }} className="flex">
          <Text
            text={`${hackathon.title}`}
            format="rubik-400"
            className="self-center h-fit"
          />
        </div>

        <div style={{ width: `${titles.end.size}%` }} className="flex">
          <Text
            text={hackathon.content}
            format="rubik-400"
            className="self-center h-fit"
          />
        </div>

        <div
          className="flex gap-x-2.5 self-center"
          style={{ width: `${titles.actions?.size}%` }}
        >
          <Tooltip text={t('info')}>
            <IconButton
              testId="klassInfoButton"
              onClick={() => toogleViewInformations('info')}
              icon={<InformationCircleIcon className="text-primary w-6 h-6 " />}
            />
          </Tooltip>

          <Tooltip text={t('hackathonStages')}>
            <IconButton
              onClick={() => toogleViewInformations('hackathonStages')}
              icon={<ClockIcon className="text-primary w-6 h-6 " />}
            />
          </Tooltip>
          <Tooltip text={tCommon('delete')}>
            <IconButton
              onClick={() => setModal(true)}
              loading={isDeleting}
              icon={<TrashIcon className="text-error w-6 h-6" />}
            />
          </Tooltip>
        </div>
      </BodyCell>

      <BodyCell className="overflow-hidden">
        <AnimatePresence>
          {viewInformations && (
            <motion.div className="w-full" {...editFormAnimation}>
              <HackathonDetails hackathon={hackathon} tab={tab} />
            </motion.div>
          )}
        </AnimatePresence>
      </BodyCell>
      <ModalDisable
        onClickConfirm={() => onDelete(hackathon.id)}
        visible={modal}
        isRequesting={isDeleting}
        translationString="modalDisableHackaton"
        onClickCancel={() => setModal(false)}
      />
    </Row>
  );
};

interface HackathonDetailsProps {
  hackathon: Hackathon;
  tab: Tabs;
}

const HackathonDetails = ({ hackathon, tab }: HackathonDetailsProps) => {
  const tabs = {
    info: <EditHackathon hackathon={hackathon} />,
    hackathonStages: <HackathonStagesManage hackathon={hackathon} />,
  };

  return (
    <div className="flex w-full justify-center mt-6">{tabs[tab || 'info']}</div>
  );
};

const ChevronUpIconWithAnimation = motion.create(ChevronUpIcon);

type HackathonStagesManageProps = {
  hackathon: Hackathon;
};

const HackathonStagesManage = ({ hackathon }: HackathonStagesManageProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'manageHackathon',
  });
  return (
    <div className="flex flex-col gap-2.5 items-start w-full">
      <Tag
        testId="classViewPill"
        text={t('hackathonStages')}
        icon={<ClockIcon className="w-6 h-6" />}
      />
      <HackathonStagesService hackathon={hackathon} />
    </div>
  );
};
